import * as angular from 'angular';
import * as _ from 'lodash';

import { IRequestService } from '../request.service';
import { IWidgetSerializationService } from '../helpers/widgetSerialization.service';
import {
    AlarmList,
    Dashboard,
    Dictionary,
    Group,
    IValueViewModel,
    ValidationPageConfiguration
} from '../../models';
import { IBindingResult, IwidgetService, IDashboardSeriesService, IBindingResultError, IRelativePathHelper, SeriesDictionary, IAlarmService } from '../';
import { toDictionary, guidEmpty } from '../../common';
import { IWidget, SeriesConfiguration } from '../../widgets';
import { IDashboardService } from './dashboard.service';

export interface IValidationPageConfigurationService {
    /**
     * Retrieves the list of validation pages configurations. Widgets configurations as serialized as strings.
     */
    getAll(): angular.IPromise<ValidationPageConfiguration[]>;
    /**
     * Retrieves the list of validation pages configurations. Widgets configurations as serialized as strings.
     * @param force Clean cache before reloading.
     */
    getAll(force: boolean): angular.IPromise<ValidationPageConfiguration[]>;
    get(groupGuid: string, viewModels: Dictionary<IValueViewModel>, groups: Dictionary<Group>): angular.IPromise<IWidget[]>;
    getValidableGroups(): angular.IPromise<Group[]>;
    getGroupsWithWidgets(): angular.IPromise<Group[]>;
    update(groupGuid: string, widgets: IWidget[]): angular.IPromise<void>;
    updateReferenceGroup(groupType: string, referenceGroupGuid: string, widgets: IWidget[]): angular.IPromise<ValidationPageConfiguration[]>;
    deserializeWidgets(widgets: IWidget[]);
    getValidationTreeGroups(): angular.IPromise<Group[]>;
}

export class ValidationPageConfigurationService implements IValidationPageConfigurationService {

    static serviceId = 'validationPageConfigurationService';

    static $inject = [
        '$q',
        'requestService',
        'widgetSerializationService',
        'widgetService',
        'dashboardSeriesService',
        'relativePathHelper',
        'alarmService',
        'dashboardService'
    ];

    constructor(
        private _$q: angular.IQService,
        private _requestService: IRequestService,
        private _widgetSerializationService: IWidgetSerializationService,
        private _widgetService: IwidgetService,
        private _dashboardSeriesService: IDashboardSeriesService,
        private _relativePathHelper: IRelativePathHelper,
        private _alarmsService: IAlarmService,
        private _dashboardService: IDashboardService
    ) {
    }

    /**
     * Retrieves the list of validation pages configurations. Widgets configurations as serialized as strings.
     * @param force Clean cache before reloading.
     */
    getAll(force?: boolean): angular.IPromise<ValidationPageConfiguration[]> {
        const url = 'validationPageConfiguration';
        if (force) {
            this._requestService.cleanCacheAtV1(url);
        }
        return this._requestService.getAtV1(url, undefined, true);
    }

    get(groupGuid: string, viewModels: Dictionary<IValueViewModel>, groups: Dictionary<Group>): angular.IPromise<IWidget[]> {
        let widgets: IWidget[] = [];
        return this._$q.all([
            this._requestService.getAtV1('validationPageConfiguration', { groupGuid: groupGuid }),
            this._alarmsService.getAll(),
            this.getAll(),
            this._dashboardService.getAll(false, true)
        ])
            .then(responses => {
                widgets = responses[0] as IWidget[];
                widgets = this.deserializeWidgets(widgets);
                const alarms = responses[1];
                const dashboards = toDictionary(responses[3] as Dashboard[]);
                const fromGroup = this.getReferenceGroup(groupGuid, groups, responses[2]);
                return this.mapSeries(widgets, viewModels, groups, dashboards, alarms, groupGuid, fromGroup);
            })
            .then(responses => this.mapWidgetList(widgets, responses));
    }

    deserializeWidgets(widgets: IWidget[]) {
        return this._widgetSerializationService.deserializeWidgets(widgets);
    }

    getValidationTreeGroups(): angular.IPromise<Group[]> {
        const promises = [
            this.getValidableGroups(),
            this.getGroupsWithWidgets()
        ];
        return this._$q.all(promises)
            .then(responses => {
                const index = toDictionary(responses[0]);
                // Add manual inputs group not present in validation groups
                return _.chain(responses[0])
                    .concat(responses[1].filter(g => !index[g.guid]))
                    .sortBy(g => g.uniqueName)
                    .value();
            });
    }

    private getReferenceGroup(groupGuid: string, groupsDic: Dictionary<Group>, pageConfig: ValidationPageConfiguration[]): Group {
        const currentGroup = groupsDic[groupGuid];
        let fromGroup: Group;
        // config by type
        if (currentGroup.configMode === 0) {
            const pc = _.find(pageConfig, c => c.name === currentGroup.type);
            if (pc && pc.referenceGroup && pc.referenceGroup !== guidEmpty) {
                fromGroup = groupsDic[pc.referenceGroup];
            }
        } else {
            fromGroup = currentGroup;
        }
        return fromGroup;
    }

    private mapSeries(
        widgets: IWidget[],
        vms: Dictionary<IValueViewModel>,
        groups: Dictionary<Group>,
        dashboards: Dictionary<Dashboard>,
        alarms: AlarmList,
        groupGuid: string,
        fromGroup?: Group
    ) {
        if (fromGroup) {
            const toGroup = groups[groupGuid];
            return this._dashboardSeriesService
                .mapSeries(widgets, vms, groups, dashboards, alarms, fromGroup, toGroup);
        }
        else {
            const promises: Dictionary<angular.IPromise<any>> = this.getWidgetsPromises(widgets, groupGuid, vms, groups, dashboards, alarms);
            return this._$q.all(promises);
        }
    }

    private getWidgetsPromises(widgets: IWidget[], groupGuid: string, vms: Dictionary<IValueViewModel>, groups: Dictionary<Group>, dashboards: Dictionary<Dashboard>, alarms: AlarmList) {
        const promises: Dictionary<angular.IPromise<any>> = {};
        widgets.forEach(w => {
            const series = this._widgetService.getSeries(w) as SeriesConfiguration[];
            if (series) {
                try {
                    let guids: string[] = series.map(s => s.guid);
                    const mapResult = this.computeMapping(groupGuid, series, vms, groups);
                    if (mapResult.guids) {
                        guids = mapResult.guids;
                    }
                    const { mapped } = mapResult;
                    const p = this._dashboardSeriesService
                        .mapPromise(w, vms, groups, dashboards, mapped, guids, alarms);
                    if (p) {
                        promises[w.guid] = p;
                    }
                }
                catch (error) {
                    console.error(error);
                }
            } else if (w.type === 'ValidatedValueSelection') {
                //TODO load series here directly
            }
        });
        return promises;
    }

    //TODO Duplicate code: dashboardLoadService.ts::97
    private mapWidgetList(widgetList: IWidget[], seriesDictionary: SeriesDictionary): IWidget[] {
        return widgetList.map(w => {
            if (seriesDictionary[w.guid]) {
                w.series = seriesDictionary[w.guid].series;
                w.map = seriesDictionary[w.guid].map;
                if (w.setError) {
                    w.setError(seriesDictionary[w.guid].error);
                }
            }
            return w;
        });
    }

    private computeMapping(
        toGroupGuid: string,
        series: SeriesConfiguration[],
        vms: Dictionary<IValueViewModel>,
        groups: { [guid: string]: Group }
    ): { mapped: IBindingResult; guids: string[] } {
        const boundSeries = series.filter(s => s.isBoundToGroup);
        const toGroup = groups[toGroupGuid];
        const mapped = this.mapTheOldWay(boundSeries, vms, groups, toGroup);
        const guids = Object.keys(mapped.map)
            .filter(k => mapped.map[k])
            .map(k => mapped.map[k])
            .concat(series.filter(s => !s.isBoundToGroup).map(s => s.guid));
        return { mapped, guids };
    }

    //TODO DUPLICATE CODE!!!! See series.service.ts::281
    private mapTheOldWay(
        series: SeriesConfiguration[],
        viewModels: Dictionary<IValueViewModel>,
        groups: Dictionary<Group>,
        toGroup: Group
    ): IBindingResult {
        const map: { [guid: string]: string } = {};
        const err: { [guid: string]: IBindingResultError } = {};
        const vmList = _.values(viewModels);

        series.forEach(s => {
            const guid = s.guid;
            const vm = viewModels[guid];
            if (vm) {
                const relative = s.relativePath;
                const newPath = this._relativePathHelper.resolveRelativePath(`${toGroup.absoluteName}/${relative}`);
                const found = vmList.find(v => v.groupAbsolutePath === newPath && v.name === vm.name);

                if (found) {
                    map[vm.guid] = found.guid;
                } else {
                    map[vm.guid] = null; // should we?
                    err[vm.guid] = {
                        currentPath: vm.groupAbsolutePath,
                        newPath: newPath,
                        relative: relative,
                        vm: vm
                    };
                }
            } else {
                const group = groups[guid];
                if (!group) {
                    err[guid] = { currentPath: 'unknown', newPath: '', relative: '' };
                }
            }
        });
        return { errors: err, map: map };
    }

    getValidableGroups(): angular.IPromise<Group[]> {
        return this._requestService.getAtV1('validationPageConfiguration/validableGroups', undefined, true);
    }

    getGroupsWithWidgets(): angular.IPromise<Group[]> {
        return this._requestService.getAtV1('validationPageConfiguration/groupsWithWidgets', undefined, true);
    }

    update(groupGuid: string, widgets: IWidget[]): angular.IPromise<void> {
        const widgetList = widgets.map(w => this._widgetSerializationService.serializeWidget(w));
        const body = {
            groupGuid: groupGuid,
            widgets: widgetList
        };
        const controller = 'validationPageConfiguration';
        return this._requestService
            .postAtV1(`${controller}`, body, undefined, true)
            .then((affectedGroupGuids: string[]) => {
                const toClean: string[] = [];
                affectedGroupGuids.forEach(g => toClean.push(`${controller}?groupGuid=${g}`));
                toClean.push(`${controller}/validableGroups`);
                toClean.push(`${controller}/groupsWithWidgets`);
                toClean.push(`${controller}`);
                toClean.forEach(url => this._requestService.cleanCacheAtV1(url));
                this._requestService.cleanCacheAtV1('validationPageConfiguration', { groupGuid: groupGuid });
            });
    }

    updateReferenceGroup(groupType: string, referenceGroupGuid: string, widgets: IWidget[]): angular.IPromise<ValidationPageConfiguration[]> {
        const controller = 'validationPageConfiguration';
        return this._requestService
            .postAtV1<{ item1: ValidationPageConfiguration[], item2: string[] }>(`${controller}/updateReferenceGroup/${groupType}/${referenceGroupGuid}`,
                { widgets: widgets.map(w => this._widgetSerializationService.serializeWidget(w)) }, undefined, true)
            .then(data => {
                this._requestService.cleanCacheAtV1(controller);
                data.item2.forEach(g => this._requestService.cleanCacheAtV1(`${controller}?groupGuid=${g}`));
                return data.item1;
            });
    }

    updateAll(vpc: ValidationPageConfiguration): angular.IPromise<ValidationPageConfiguration[]> {
        throw new Error('Method not implemented.');
    }
}